/**
 * The filter panel (Horizon's `#filters-drawer`, birrot-collection.css).
 *
 * - Focus: theme-drawer focuses the close button on open, and Safari draws
 *   :focus-visible for that however the panel was opened (Chromium after a
 *   keyboard open): a box round ×. The dialog itself takes focus
 *   instead (birrot-drawer-focus.js, on the drawer's
 *   data-birrot-focus-dialog), as the header's dialogs do; Tab goes on
 *   to Close.
 * - Applying: Horizon applies a filter the moment it is ticked (the URL,
 *   the grid and the panel all re-render from the server). Here a choice
 *   waits until "See N items": until then only the panel is re-rendered,
 *   from the section HTML for the pending URL (the same fetch Horizon
 *   makes, so the counts, the disabled values and the See N items count
 *   follow the pending choice), and the grid and the URL stay as they
 *   are; a Sort choice, which calls the same update, waits with them. See
 *   N items applies the pending choice (Horizon's own update,
 *   which then re-renders the whole section, panel included) and
 *   Horizon's own on:click closes the panel. Clear all applies the bare
 *   URL and closes. Closed any other way, the choice is simply left: as
 *   the panel next opens its inputs are put back to the page's own URL
 *   at once and the counts rendered from it again
 *   (not as it closes: Horizon's delegated on:click runs before this
 *   module's click listener, and a render started there would land, a
 *   microtask later, before See N items had read the form). The section
 *   HTML renders every group closed (snippets/list-filter.liquid), but
 *   Horizon's morph carries a `<details>`' open state over from the
 *   element on the page (morph.js, updateNode), so the group being
 *   ticked in stays open through each re-render.
 *   It works by replacing the drawer form's `updateFilters` (an instance
 *   field of facets.js's FacetsFormComponent, what every input, Clear and
 *   Sort control calls) and `updateFiltersByURL` (what Clear all calls)
 *   on the instance, once, as the drawer opens; the originals are kept
 *   to apply with.
 *
 * Closing on a click beside the panel needs nothing here: the wash is part
 * of the dialog, and theme-drawer closes it on a click outside its rect.
 *
 * Delegated from the document, so a drawer the facets morph replaces is
 * still handled.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';

const DRAWER = '#filters-drawer';

/** @param {Event} event */
const drawerOf = (event) => (event.target instanceof Element ? event.target.closest(DRAWER) : null);

/**
 * Re-render the panel (its form and its footer, not the dialog itself,
 * which carries the open state and focus) from the
 * section's HTML for `url`, leaving the grid and the page URL alone.
 * @param {Element} drawer
 * @param {URL} url
 */
async function renderPanel(drawer, url) {
  const form = /** @type {any} */ (drawer.querySelector('facets-form-component'));
  if (!form) return;
  // A later render (an open, an apply) outranks one still in flight.
  const token = (/** @type {any} */ (drawer).birrotRender = Symbol());
  const next = await fetchSection(form.sectionId, url);
  if (!next || /** @type {any} */ (drawer).birrotRender !== token) return;
  for (const selector of ['facets-form-component', '.facets__drawer-actions']) {
    const current = drawer.querySelector(selector);
    const incoming = next.querySelector(`${DRAWER} ${selector}`);
    if (!current || !incoming) continue;
    // A pending choice with no products: blocks/filters.liquid leaves See N
    // items out, and the grid behind still shows the old products. Keep the
    // button, disabled, with the locale's own label for a count of 0
    // (`data-birrot-see-none`, which blocks/filters.liquid prints on the
    // actions row), until a later render brings it back.
    const button = current.querySelector('.facets__see-results');
    if (button && !incoming.querySelector('.facets__see-results')) {
      const kept = /** @type {HTMLButtonElement} */ (button.cloneNode(true));
      kept.disabled = true;
      const none = incoming.getAttribute('data-birrot-see-none') ?? current.getAttribute('data-birrot-see-none');
      if (none) kept.textContent = none;
      incoming.append(kept);
    }
    // Root included (MORPH_OPTIONS is children only): the form's own
    // data-results-count is the pending count.
    morph(current, incoming, { ...MORPH_OPTIONS, childrenOnly: false });
  }
}

/**
 * The section's document for `url`, or null if it cannot be had. The
 * renderer caches whatever body the fetch returns, status unchecked (the
 * dev proxy's 502s included), so a body with no drawer in it is fetched
 * again past the cache, which also puts the good copy in the cache for
 * the apply (Horizon's own update reads it).
 * @param {string} sectionId
 * @param {URL} url
 * @returns {Promise<Document | null>}
 */
async function fetchSection(sectionId, url) {
  for (const useCache of [true, false]) {
    let html;
    try {
      html = await sectionRenderer.getSectionHTML(sectionId, useCache, url);
    } catch {
      return null; // the tick stays live in the form; the next change renders again
    }
    const next = new DOMParser().parseFromString(html, 'text/html');
    if (next.querySelector(`${DRAWER} facets-form-component`)) return next;
  }
  return null;
}

/**
 * Put the form's inputs back to the page's own URL at once, before any
 * render: a choice dropped by a close would otherwise stay ticked, and be
 * sent along with a tick made before the open-time render lands. A name
 * the URL does not carry (`sort_by` when the page sorts by its default)
 * goes back to what the server rendered, its `checked` attribute, which
 * the last apply's morph keeps current.
 * @param {Element} drawer
 */
function resetInputs(drawer) {
  const params = new URL(window.location.href).searchParams;
  for (const input of drawer.querySelectorAll('input')) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (input.type === 'checkbox' || input.type === 'radio') {
      input.checked = params.has(input.name) ? params.getAll(input.name).includes(input.value) : input.defaultChecked;
    } else if (input.name) {
      input.value = params.get(input.name) ?? '';
    }
  }
}

/**
 * Put the drawer's form into pending mode, once per element.
 * @param {Element} drawer
 */
function deferApplying(drawer) {
  const form = /** @type {any} */ (drawer.querySelector('facets-form-component'));
  if (!form || form.birrotApply) return;
  // Not upgraded yet (an open before facets.js has defined it): patched
  // once it is, or its instance field would put the stock method back.
  if (typeof form.updateFilters !== 'function') {
    customElements.whenDefined('facets-form-component').then(() => deferApplying(drawer));
    return;
  }

  form.birrotApply = form.updateFilters;
  form.updateFilters = () => {
    /** @type {any} */ (drawer).birrotPending = true;
    const url = new URL(window.location.href);
    url.search = '';
    for (const [key, value] of form.createURLParameters()) url.searchParams.append(key, value);
    renderPanel(drawer, url);
  };

  // Clear all: applied at once, and the panel closes.
  const applyURL = form.updateFiltersByURL.bind(form);
  form.updateFiltersByURL = (/** @type {string} */ href) => {
    /** @type {any} */ (drawer).birrotPending = false;
    /** @type {any} */ (drawer).birrotRender = null;
    applyURL(href);
    /** @type {any} */ (drawer).close?.();
  };
}

document.addEventListener('theme-drawer:open', (event) => {
  const drawer = drawerOf(event);
  const dialog = drawer?.querySelector('dialog');
  if (!drawer || !dialog) return;

  // theme-drawer fires the same event again when open() is called on a
  // drawer already open (its #bringToFront: the Filters link reached
  // again, or a dialog opened over it), with the shopper's ticks still
  // live. Only the open after a close drops what was left.
  if (/** @type {any} */ (drawer).birrotOpen) return;
  /** @type {any} */ (drawer).birrotOpen = true;

  deferApplying(drawer);
  // A choice left behind by a close without See N items is dropped here:
  // the inputs at once, the counts and the footer by a render.
  if (/** @type {any} */ (drawer).birrotPending) {
    /** @type {any} */ (drawer).birrotPending = false;
    resetInputs(drawer);
    renderPanel(drawer, new URL(window.location.href));
  }
});

document.addEventListener('theme-drawer:close', (event) => {
  const drawer = drawerOf(event);
  if (drawer) /** @type {any} */ (drawer).birrotOpen = false;
});

// See N items: apply the pending choice; Horizon's own on:click closes.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('.facets__see-results')) return;
  const drawer = drawerOf(event);
  const form = /** @type {any} */ (drawer?.querySelector('facets-form-component'));
  if (!drawer || !form?.birrotApply) return;
  /** @type {any} */ (drawer).birrotPending = false;
  /** @type {any} */ (drawer).birrotRender = null;
  form.birrotApply();
});
